import { diffDocuments, parseLegal } from '@westhetics/legal'
import {
  ActionError,
  AttentionBlock,
  Button,
  Checkbox,
  ChoiceRow,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  ListRow,
  ListRows,
  PageHeader,
  PageTitle,
  ReadingColumn,
  Skeleton,
  Textarea,
  toast,
} from '@westhetics/ui'
import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { routes } from '@/routes'
import {
  usePublishVersion,
  useTemplate,
  useTemplateVersion,
  useTemplateVersions,
  type LintDiagnostic,
  type Template,
} from './api'
import { diffHeadline, summaryKindLabel, summaryStatusLabel } from './diff'
import { suggestVersionLabel } from './labels'
import { publishIssues, todayInSaoPaulo } from './publish-check'

/**
 * Publicar versão (docs/areas/documentos-e-assinaturas.md, "Publicar versão"; RN-DOC-12 a 14 e 47).
 * Publicar é irreversível e vale na hora para todas as clínicas: por isso a pessoa lê o que mudou
 * e a conferência do dialeto antes do botão. Rótulo, data e revisão são campos; ninguém escreve YAML.
 */
export function PublishTemplatePage() {
  const { id = '' } = useParams()
  const template = useTemplate(id)
  const versions = useTemplateVersions(id)
  const current = template.data?.currentVersion
  const published = useTemplateVersion(id, current?.number)

  const error = template.error ?? versions.error ?? published.error
  const ready =
    template.data && versions.data && (!current || published.data) ? template.data : null

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Contratos e termos', href: routes.admin.templates },
          { label: template.data?.name ?? '…', href: routes.admin.template(id, 'editor') },
          { label: 'publicar' },
        ]}
      />
      <Page>
        <ReadingColumn wide>
          {error ? (
            <LoadError
              title="Não conseguimos preparar a publicação"
              error={error}
              onRetry={() => {
                void template.refetch()
                void versions.refetch()
                void published.refetch()
              }}
            />
          ) : !ready ? (
            <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
              <Skeleton className="h-[26px] w-[320px] max-w-full" />
              <Skeleton className="h-[180px] w-full" />
              <Skeleton className="h-[220px] w-full" />
            </div>
          ) : (
            <PublishForm
              template={ready}
              takenLabels={(versions.data ?? []).map((version) => version.label)}
              publishedMarkdown={published.data?.markdown ?? null}
            />
          )}
        </ReadingColumn>
      </Page>
    </>
  )
}

function PublishForm({
  template,
  takenLabels,
  publishedMarkdown,
}: {
  template: Template
  takenLabels: string[]
  publishedMarkdown: string | null
}) {
  const navigate = useNavigate()
  const publish = usePublishVersion(template.id)
  const current = template.currentVersion
  const isFirst = !current
  const draft = template.draft?.markdown ?? ''
  const editor = routes.admin.template(template.id, 'editor')

  const [label, setLabel] = useState(() => suggestVersionLabel(takenLabels))
  const [updateNote, setUpdateNote] = useState('')
  // A revisão da versão anterior sugere quem revisa esta; a marca da carga do sistema anterior não (RN-DOC-53).
  const [reviewedBy, setReviewedBy] = useState(
    template.isLegalReviewPending ? '' : (current?.reviewedBy ?? ''),
  )
  const [reviewedAt, setReviewedAt] = useState('')
  const [requiresIcp, setRequiresIcp] = useState(current?.requiresIcpCertificate ?? false)
  const [submitted, setSubmitted] = useState(false)

  const diff = useMemo(
    () => (publishedMarkdown !== null ? diffDocuments(publishedMarkdown, draft) : null),
    [publishedMarkdown, draft],
  )
  // A mesma conferência que a API faz antes de publicar (RN-DOC-12), para ninguém descobrir no botão.
  const lint = useMemo(() => parseLegal(draft, { catalog: true }).diagnostics, [draft])
  const lintErrors = lint.filter((item) => item.severity === 'error')
  const lintWarnings = lint.filter((item) => item.severity === 'warning')

  // A data da publicação é a de São Paulo, como na API.
  const today = todayInSaoPaulo()
  const nothingToPublish = !template.hasUnpublishedChanges || draft.trim() === ''
  // O que a API confere além do dialeto: cabeçalho, tamanho e o bloco de atualização (RN-DOC-13).
  const issues = useMemo(
    () =>
      nothingToPublish
        ? []
        : publishIssues({
            draft,
            templateType: template.type,
            number: template.versionCount + 1,
            today,
          }),
    [nothingToPublish, draft, template.type, template.versionCount, today],
  )
  const blockers = [
    ...lintErrors.map((item) => `${item.message}${item.line ? ` (linha ${item.line})` : ''}`),
    ...issues,
  ]
  const errors = {
    label:
      label.trim() === ''
        ? 'Dê um rótulo à versão, como 2026.10.'
        : /\s/.test(label.trim()) || label.trim().length > 20
          ? 'O rótulo tem até 20 caracteres, sem espaço.'
          : takenLabels.includes(label.trim())
            ? `A versão ${label.trim()} já foi publicada. Escolha outro rótulo.`
            : null,
    updateNote:
      !isFirst && (updateNote.trim().length < 10 || updateNote.trim().length > 500)
        ? 'Diga em uma linha o que mudou nesta versão, em 10 a 500 caracteres.'
        : null,
    reviewedBy: reviewedBy.trim().length < 3 ? 'Registre quem revisou antes de publicar.' : null,
    reviewedAt:
      reviewedAt === ''
        ? 'Registre quando foi a revisão.'
        : reviewedAt > today
          ? 'A data da revisão não pode ser futura.'
          : null,
  }
  const hasFormError = Object.values(errors).some(Boolean)
  const blocked = blockers.length > 0 || nothingToPublish
  const serverFields = fieldErrors(publish.error)
  const serverDiagnostics =
    publish.error && isApiError(publish.error)
      ? ((publish.error.details.diagnostics as LintDiagnostic[] | undefined) ?? [])
      : []

  function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    if (hasFormError || blocked) return
    publish.mutate(
      {
        label: label.trim(),
        reviewedBy: reviewedBy.trim(),
        reviewedAt,
        ...(isFirst ? {} : { updateNote: updateNote.trim() }),
        requiresIcpCertificate: requiresIcp,
      },
      {
        onSuccess: () => {
          toast(`Versão ${label.trim()} publicada — vale nas próximas gerações`)
          navigate(routes.admin.template(template.id, 'versions'))
        },
      },
    )
  }

  const clinics = template.usage.clinicCount
  const reach =
    clinics === 0
      ? 'Ainda nenhuma clínica usa este documento.'
      : clinics === 1
        ? 'Passa a valer agora na clínica que usa este documento.'
        : `Passa a valer agora nas ${clinics} clínicas que usam este documento.`

  return (
    <form onSubmit={submit} noValidate>
      <PageTitle
        title={isFirst ? 'Publicar a primeira versão' : 'Publicar versão'}
        subtitle={`${template.name} · leia o que muda antes de publicar.`}
      />

      {nothingToPublish ? (
        <ActionError
          className="mt-6"
          title="Não há alteração para publicar"
          description="O rascunho está igual à versão vigente. Altere o documento no editor e volte aqui."
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to={editor}>Voltar ao editor</Link>
            </Button>
          }
        />
      ) : null}

      {publish.isError ? (
        <ActionError
          className="mt-6"
          title="A versão não foi publicada"
          description={
            <>
              {serverFields.markdown ?? errorMessage(publish.error)}
              {serverDiagnostics.length > 1 ? (
                <ul className="mt-2 list-disc pl-5">
                  {serverDiagnostics.map((item, index) => (
                    <li key={index}>
                      {item.message}
                      {item.line ? ` (linha ${item.line})` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          }
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to={editor}>Voltar ao editor</Link>
            </Button>
          }
        />
      ) : null}

      <section className="mt-6" aria-labelledby="publish-changes">
        <h2 id="publish-changes" className="eyebrow">
          {isFirst ? 'O que será publicado' : `O que muda da versão ${current.label} para esta`}
        </h2>
        {isFirst || !diff ? (
          <p className="font-body text-body text-secondary mt-2 font-light">
            É a primeira versão deste documento: o texto inteiro do rascunho passa a ser o que as
            clínicas usam.
          </p>
        ) : (
          <>
            <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">
              {diffHeadline(diff)} ·{' '}
              <Link
                className="border-hairline-heavy hover:border-ink text-ink border-b"
                to={`${routes.admin.template(template.id, 'compare')}?from=${current.number}&to=draft`}
              >
                ver o texto
              </Link>
            </p>
            <ListRows className="mt-3">
              {diff.summary.length === 0 ? (
                <ListRow
                  title="Só o texto mudou"
                  meta="Nenhuma cláusula, bloco ou variável entrou ou saiu."
                />
              ) : (
                diff.summary.map((entry, index) => (
                  <ListRow
                    key={`${entry.kind}-${entry.label}-${index}`}
                    eyebrow={summaryKindLabel(entry)}
                    title={entry.label}
                    trailing={
                      <span className="font-body text-eyebrow text-secondary font-light tracking-normal">
                        {summaryStatusLabel(entry)}
                      </span>
                    }
                  />
                ))
              )}
            </ListRows>
          </>
        )}
      </section>

      <section className="mt-6" aria-labelledby="publish-check">
        <h2 id="publish-check" className="eyebrow">
          Conferência
        </h2>
        {blockers.length > 0 ? (
          <AttentionBlock
            className="mt-3"
            variant="plum"
            title={
              blockers.length === 1
                ? '1 erro impede a publicação'
                : `${blockers.length} erros impedem a publicação`
            }
          >
            <ul>
              {blockers.map((message, index) => (
                <li key={index}>· {message}</li>
              ))}
            </ul>
            <Link className="border-ink mt-2 inline-block border-b" to={editor}>
              Voltar ao editor para corrigir
            </Link>
          </AttentionBlock>
        ) : (
          <AttentionBlock
            className="mt-3"
            variant="lilac"
            title="O documento está dentro do dialeto"
          >
            {lintWarnings.length === 0 ? (
              'Nenhum aviso. Blocos obrigatórios, variáveis e títulos conferidos.'
            ) : (
              <ul>
                {lintWarnings.map((item, index) => (
                  <li key={index}>
                    · {item.message}
                    {item.line ? ` (linha ${item.line})` : ''}
                  </li>
                ))}
              </ul>
            )}
          </AttentionBlock>
        )}
      </section>

      <FormBlock
        className="mt-6"
        title="Versão"
        note="Entram no cabeçalho do documento e no histórico. A data da publicação é a de hoje."
      >
        <div className="flex flex-col gap-4">
          <FieldGrid>
            <Field
              label="Rótulo da versão"
              error={(submitted && errors.label) || serverFields.label}
              hint="Ano e mês da publicação. Não repete um rótulo já publicado."
            >
              <Input
                value={label}
                maxLength={20}
                onChange={(event) => setLabel(event.target.value)}
              />
            </Field>
          </FieldGrid>
          {isFirst ? null : (
            <Field
              label="Nota da versão"
              meta={`${updateNote.trim().length}/500`}
              error={(submitted && errors.updateNote) || serverFields.updateNote}
              hint="Diga em uma linha o que mudou. É o que as clínicas e o histórico vão ler."
            >
              <Textarea
                value={updateNote}
                maxLength={500}
                onChange={(event) => setUpdateNote(event.target.value)}
              />
            </Field>
          )}
          <FieldGrid>
            <Field
              label="Revisado por"
              error={(submitted && errors.reviewedBy) || serverFields.reviewedBy}
              hint={
                template.isLegalReviewPending
                  ? 'Este documento veio do sistema anterior sem revisão jurídica. Registre quem revisou este texto: é o que o tira da fila.'
                  : 'Quem respondeu juridicamente pelo texto.'
              }
            >
              <Input
                value={reviewedBy}
                maxLength={120}
                placeholder="Nome e registro na OAB"
                onChange={(event) => setReviewedBy(event.target.value)}
              />
            </Field>
            <Field
              label="Revisado em"
              error={(submitted && errors.reviewedAt) || serverFields.reviewedAt}
            >
              <Input
                type="date"
                max={today}
                value={reviewedAt}
                onChange={(event) => setReviewedAt(event.target.value)}
              />
            </Field>
          </FieldGrid>
          <ChoiceRow
            control={
              <Checkbox
                checked={requiresIcp}
                onCheckedChange={(checked) => setRequiresIcp(checked === true)}
              />
            }
            description="A assinatura deste documento passa a exigir certificado digital."
          >
            Exige certificado ICP-Brasil
          </ChoiceRow>
        </div>
      </FormBlock>

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <Button variant="outline" asChild>
          <Link to={editor}>Voltar ao editor</Link>
        </Button>
        <Button
          type="submit"
          disabled={blocked}
          loading={publish.isPending}
          loadingLabel="Publicando a versão…"
        >
          {blockers.length > 0
            ? 'Corrija os erros para publicar'
            : nothingToPublish
              ? 'Nada para publicar'
              : `Publicar versão ${label.trim()}`}
        </Button>
      </div>
      <p className="font-body text-label mt-3 text-right text-[12px] leading-[1.6] font-light">
        {reach} O que já foi emitido não muda. Uma versão publicada não se edita: corrigir é
        publicar outra.
      </p>
    </form>
  )
}
